'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sun, Sunset, RefreshCw, ChevronRight, Info, AlertCircle, FileText } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { DailySoaringForecast } from '@/lib/mockWeatherData';
import { fetchSoaringForecast } from '@/lib/openMeteoApi';
import AerodromeSearch from './AerodromeSearch';
import SoaringBadge from '@/components/ui/SoaringBadge';
import SoaringConditionsGrid from './SoaringConditionsGrid';
import HourlyTimelineChart from './HourlyTimelineChart';
import WindProfileTable from './WindProfileTable';
import StormRiskPanel from './StormRiskPanel';
import RainRiskPanel from './RainRiskPanel';
import Link from 'next/link';
import { useAerodrome } from '@/context/AerodromeContext';

function verdictLabel(verdict: DailySoaringForecast['verdict']): string {
  if (verdict === 'favorable') return 'Bonne journée pour le vol à voile';
  if (verdict === 'marginal') return 'Conditions marginales — prudence recommandée';
  if (verdict === 'storm-risk') return 'Risque orageux — vol déconseillé';
  return 'Conditions défavorables au vol';
}

function scoreLabel(forecast: DailySoaringForecast): string {
  if (forecast.verdict === 'storm-risk') return 'Risque orageux - vol deconseille';
  if (forecast.verdict === 'unflyable' || forecast.qualityScore < 15) return 'Conditions defavorables au vol';
  if (forecast.qualityScore > 75) return 'Journee excellente';
  if (forecast.qualityScore >= 50) return 'Bonne journee';
  return 'Journee moyenne';
}

export default function DashboardContent() {
  const { selectedAerodrome, setSelectedAerodrome, hydrated } = useAerodrome();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forecast, setForecast] = useState<DailySoaringForecast | null>(null);
  const lastManualRefreshRef = useRef(0);
  const lastLoadedIcaoRef = useRef('');

  const loadForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { today } = await fetchSoaringForecast(selectedAerodrome);
      if (today?.cacheStatus === 'rate-limited' && (!today.hourly || today.hourly.length === 0)) {
        setError(today.cacheMessage ?? 'Open-Meteo limite temporairement les requêtes. Veuillez réessayer dans quelques minutes.');
      } else {
        setForecast(today);
        lastLoadedIcaoRef.current = selectedAerodrome.icao;
      }
    } catch (err) {
      console.warn('Open-Meteo fetch warning:', err);
      const message = err instanceof Error
        ? err.message
        : 'Impossible de charger les données météo. Vérifiez votre connexion.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [selectedAerodrome]);

  useEffect(() => {
    if (!hydrated) return;
    if (forecast && lastLoadedIcaoRef.current === selectedAerodrome.icao) return;
    loadForecast();
  }, [forecast, hydrated, loadForecast, selectedAerodrome.icao]);

  function handleAerodromeSelect(a: typeof selectedAerodrome) {
    setSelectedAerodrome(a);
    toast.success(`Terrain sélectionné : ${a.icao} — ${a.name}`, { duration: 3000 });
  }

  function handleRefresh() {
    const now = Date.now();
    const elapsed = now - lastManualRefreshRef.current;
    if (elapsed < 45_000) {
      const waitSeconds = Math.ceil((45_000 - elapsed) / 1000);
      toast.info(`Actualisation disponible dans ${waitSeconds} s`, { duration: 2500 });
      return;
    }
    lastManualRefreshRef.current = now;
    loadForecast().then(() => {
      toast.success('Données actualisées depuis Open-Meteo', { duration: 2000 });
    });
  }

  const lastUpdatedLabel = forecast?.lastUpdated
    ? new Date(forecast.lastUpdated).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
    : null;
  const cacheLabel =
    forecast?.cacheStatus === 'rate-limited' ?'Actualisation temporairement bloquee par Open-Meteo'
      : forecast?.cacheStatus === 'cached' || forecast?.cacheStatus === 'stale'
        ? 'Donnees mises en cache' :'Donnees fraiches';

  return (
    <div className="min-h-screen px-3 sm:px-4 md:px-6 xl:px-8 2xl:px-10 py-4 sm:py-6 max-w-screen-2xl mx-auto">
      <Toaster position="bottom-right" theme="dark" />

      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Météo Planeur
          </h1>
          <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {selectedAerodrome.icao} · {selectedAerodrome.name} · {forecast?.modelSourceLabel ?? 'Open-Meteo'}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex-1 sm:flex-none">
            <AerodromeSearch selected={selectedAerodrome} onSelect={handleAerodromeSelect} />
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 active:scale-95 disabled:opacity-50 touch-manipulation"
            style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border)', color: '#ffffff' }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {!loading && <span className="hidden sm:inline">Actualiser</span>}
          </button>
        </div>
      </div>

      {forecast && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span
            className="px-2.5 py-1 rounded-lg"
            style={{
              backgroundColor: forecast.cacheStatus === 'rate-limited' ?'color-mix(in srgb, var(--warning-orange) 16%, transparent)' :'color-mix(in srgb, var(--info-blue) 14%, transparent)',
              border: '1px solid var(--border)',
              color: forecast.cacheStatus === 'rate-limited' ? 'var(--warning-orange)' : 'var(--text-secondary)',
            }}
          >
            {cacheLabel}
          </span>
          {lastUpdatedLabel && (
            <span className="px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              Derniere mise a jour : {lastUpdatedLabel}
            </span>
          )}
          {forecast.cacheMessage && (
            <span className="px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              {forecast.cacheMessage}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-4 animate-pulse">
          <div className="h-32 rounded-xl" style={{ backgroundColor: 'var(--card)' }} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[1,2,3,4,5,6].map((i) => (
              <div key={`skel-${i}`} className="h-24 rounded-xl" style={{ backgroundColor: 'var(--card)' }} />
            ))}
          </div>
          <div className="h-64 rounded-xl" style={{ backgroundColor: 'var(--card)' }} />
          <div className="flex items-center justify-center gap-2 py-4">
            <RefreshCw size={16} className="animate-spin" style={{ color: 'var(--accent-cyan)' }} />
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Chargement des données Open-Meteo…</span>
          </div>
        </div>
      ) : error ? (
        <div
          className="flex flex-col items-center justify-center gap-4 py-16 rounded-2xl"
          style={{ backgroundColor: 'var(--card)', border: `1px solid color-mix(in srgb, ${error.includes('limite') ? 'var(--warning-orange)' : 'var(--danger-red)'} 28%, transparent)` }}
        >
          <AlertCircle size={40} style={{ color: error.includes('limite') ? 'var(--warning-orange)' : 'var(--danger-red)' }} />
          <p className="text-sm text-center px-4" style={{ color: error.includes('limite') ? 'var(--warning-orange)' : 'var(--danger-red)' }}>{error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 rounded-xl text-sm font-medium touch-manipulation"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-cyan) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent-cyan) 34%, transparent)',
              color: 'var(--accent-cyan)',
            }}
          >
            Réessayer
          </button>
        </div>
      ) : forecast ? (
        <div className="space-y-4 animate-fade-in">
          {/* Hero verdict card */}
          <div
            className="rounded-2xl p-4 sm:p-5 flex flex-col gap-4"
            style={{
              background: 'var(--surface-elevated)',
              border: '1px solid var(--card-border)',
              boxShadow: 'var(--shadow-soft)',
            }}
          >
            <div className="flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <SoaringBadge verdict={forecast.verdict} size="lg" />
                  <span
                    className="font-mono-data text-sm px-2.5 py-1 rounded-lg"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--info-blue) 16%, transparent)', color: 'var(--info-blue)' }}
                  >
                    Score : {forecast.qualityScore}/100
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {selectedAerodrome.icao} · {selectedAerodrome.name}
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {scoreLabel(forecast)}
                  {forecast.convergence && ' · Convergence active'}
                  {forecast.waveConditions && ' · Onde possible'}
                </p>
              </div>
            </div>

            {/* Sun times — full width on mobile */}
            <div className="flex items-center justify-around sm:justify-start sm:gap-6 flex-shrink-0">
              <div className="flex flex-col items-center gap-1">
                <Sun size={20} style={{ color: '#fbbf24' }} />
                <span className="font-mono-data text-lg font-bold" style={{ color: '#fbbf24' }}>
                  {forecast.sunrise}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Lever</span>
              </div>
              <div
                className="flex flex-col items-center gap-1 px-4 sm:px-6 py-3 rounded-xl"
                style={{ backgroundColor: 'color-mix(in srgb, var(--success-green) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--success-green) 28%, transparent)' }}
              >
                <span className="text-xs font-medium" style={{ color: 'var(--success-green)' }}>Fenêtre vol</span>
                <span className="font-mono-data text-sm font-bold" style={{ color: 'var(--success-green)' }}>
                  {forecast.convectionStart !== '--' ? forecast.convectionStart : '--'} → {forecast.convectionEnd !== '--' ? forecast.convectionEnd : '--'}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Thermiques actifs</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Sunset size={20} style={{ color: '#f97316' }} />
                <span className="font-mono-data text-lg font-bold" style={{ color: '#f97316' }}>
                  {forecast.sunset}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Coucher</span>
              </div>
            </div>
          </div>

          {/* Info banner */}
          <div
            className="flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <Info size={13} style={{ color: '#7ec8e3' }} />
            <span style={{ color: 'var(--text-secondary)' }}>
              Les prévisions s'arrêtent au coucher du soleil ({forecast.sunset}). {forecast.modelSourceNote ?? 'Données Open-Meteo indicatives.'}
            </span>
          </div>

          <Link
            href="/daily-briefing"
            className="inline-flex w-full sm:w-fit items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-150 hover:opacity-90 touch-manipulation"
            style={{ backgroundColor: 'color-mix(in srgb, var(--info-blue) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--info-blue) 35%, transparent)', color: 'var(--info-blue)' }}
          >
            <FileText size={16} />
            Briefing quotidien
          </Link>

          {/* Conditions grid */}
          <SoaringConditionsGrid forecast={forecast} />

          {/* Charts + Wind + Storm row — stacked on mobile */}
          <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
            <div
              className="xl:col-span-2 rounded-xl p-4 sm:p-5"
              style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}
            >
              <HourlyTimelineChart
                data={forecast.hourly}
                convectionStart={forecast.convectionStart}
                convectionEnd={forecast.convectionEnd}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}
              >
                <WindProfileTable forecast={forecast} />
              </div>
              <RainRiskPanel forecast={forecast} />
              <StormRiskPanel forecast={forecast} />
            </div>
          </div>

          {/* Bottom quick links — 2 cols on mobile, 4 on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { href: '/interactive-emagram', label: 'Voir l\'Émagramme', sub: 'Sondage atmosphérique' },
              { href: '/multi-day-forecast', label: 'Prévisions 7 jours', sub: 'Planifier la semaine' },
              { href: '/meteorological-map', label: 'Carte Météo', sub: `Rayon 100 km` },
              { href: '/beginner-s-guide', label: 'Guide Débutant', sub: 'Comprendre la météo' },
            ].map((link) => (
              <Link
                key={`quicklink-${link.href}`}
                href={link.href}
                className="flex items-center justify-between px-3 sm:px-4 py-3 rounded-xl group transition-all duration-150 hover:border-opacity-60 touch-manipulation"
                style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}
              >
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium group-hover:text-[#7ec8e3] transition-colors truncate" style={{ color: '#d4e8f0' }}>
                    {link.label}
                  </p>
                  <p className="text-xs mt-0.5 hidden sm:block" style={{ color: '#4a6070' }}>{link.sub}</p>
                </div>
                <ChevronRight size={14} style={{ color: '#4a6070' }} className="group-hover:text-[#7ec8e3] transition-colors flex-shrink-0 ml-1" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
